import {
  ChatRequest,
  ChatResponse,
  RateLimitStatus,
  ChatThread,
  ChatThreadsResponse,
  ChatMessagesResponse,
  CreateChatThreadRequest,
  CreateChatThreadResponse,
} from "@shared/api";
import type { ListSharesResponse, Share, Timesheet, TimeEntry } from "@shared/api";

export type { Timesheet, TimeEntry };

export type CreateTimesheetEntryInput = {
  project_id: string;
  task_id?: string | null;
  entry_date: string;
  hours: number;
  description?: string | null;
};

export type UpdateTimesheetEntryInput = {
  hours?: number;
  task_id?: string | null;
  description?: string | null;
};
import type {
  Task,
  TaskType,
  TaskDiscipline,
  CreateTaskInput,
  UpdateTaskInput,
  Note,
  CreateNoteInput,
  UpdateNoteInput,
  CompanySummary,
  CompanyMember,
  ProjectSummary,
  ProjectMember,
  ProjectPrimary,
  CreateProjectInput,
  UpdateProjectInput,
  TrackerDisciplineState,
  TrackerMilestoneState,
  TrackerTaskState,
  ProjectTrackerState,
  OwnerRequirementState,
  OwnerRequirementDoc,
  OwnerRequirementTemplate,
} from "./types";

export type UserHeaders = { id?: string; role?: string; plan?: string; email?: string; token?: string };

function isJwtExpTimestampFailure(bodyText: string): boolean {
  const s = String(bodyText || "").toLowerCase();
  // Backend error observed: {"error":"\"exp\" claim timestamp check failed"}
  return s.includes("exp") && s.includes("claim") && s.includes("timestamp") && s.includes("failed");
}

async function refreshClerkJwtIfPossible(): Promise<string> {
  try {
    const g: any = globalThis as any;
    const fn = g?.__calceraClerkRefreshJwt;
    if (typeof fn !== "function") return "";
    const t = await fn();
    return typeof t === "string" ? t : "";
  } catch {
    return "";
  }
}

function getJwtExpMs(token: string): number | null {
  try {
    const t = String(token || "").trim();
    if (!t) return null;
    const parts = t.split(".");
    if (parts.length < 2) return null;
    const payloadB64Url = parts[1] || "";
    const payloadB64 = payloadB64Url.replace(/-/g, "+").replace(/_/g, "/");
    const padLen = (4 - (payloadB64.length % 4)) % 4;
    const padded = payloadB64 + "=".repeat(padLen);
    const json = JSON.parse(atob(padded));
    const expSeconds = typeof json?.exp === "number" ? json.exp : Number(json?.exp);
    if (!Number.isFinite(expSeconds) || expSeconds <= 0) return null;
    return expSeconds * 1000;
  } catch {
    return null;
  }
}

function pickFreshestToken(a: string, b: string): string {
  const aa = String(a || "").trim();
  const bb = String(b || "").trim();
  if (!aa) return bb;
  if (!bb) return aa;

  const aExp = getJwtExpMs(aa);
  const bExp = getJwtExpMs(bb);

  // If both decode, prefer the one that expires later.
  if (aExp && bExp) return aExp >= bExp ? aa : bb;

  // If only one decodes, prefer the decodable one (more likely to be a Clerk JWT).
  if (aExp && !bExp) return aa;
  if (bExp && !aExp) return bb;

  // Fallback: preserve the previous precedence (explicit user token wins).
  return aa;
}

function compactNoteInput<T extends Record<string, any>>(input: T): T {
  // Backend CreateNoteInput expects `tags?: string[]` (optional) rather than `tags: null`.
  // Same for `title?`: omit it if null to satisfy stricter validators.
  const out: any = { ...input };
  if ("tags" in out && out.tags == null) delete out.tags;
  if ("title" in out && out.title == null) delete out.title;
  return out as T;
}

function isApiDebugEnabled(): boolean {
  try {
    if (typeof window === "undefined") return false;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("debugApi") === "1" || sp.get("debugApi") === "true") return true;
    return window.localStorage.getItem("calcera:debugApi") === "1";
  } catch {
    return false;
  }
}

function headersToLogObject(headers: Headers): Record<string, string> {
  const out: Record<string, string> = {};
  headers.forEach((value, key) => {
    const lower = key.toLowerCase();
    if (lower === "authorization") {
      // Never log raw bearer tokens.
      const v = String(value || "");
      const isBearer = /^bearer\s+/i.test(v);
      out[key] = isBearer ? "Bearer <redacted>" : "<redacted>";
      return;
    }
    out[key] = value;
  });
  return out;
}

function buildHeaders(user?: UserHeaders): Headers {
  const h = new Headers();
  h.set("content-type", "application/json");
  try {
    const backendBase = (import.meta as any)?.env?.VITE_BACKEND_URL as string | undefined;
    const apiKey = (import.meta as any)?.env?.VITE_BACKEND_API_KEY as string | undefined;
    if (backendBase && apiKey) {
      h.set("x-api-key", apiKey);
    }
  } catch {}
  // Prefer Clerk Bearer token auth; fall back to legacy x-user-* headers.
  // We support three sources:
  // - explicit `user.token` (preferred when available)
  // - a global token bridge set by `client/App.tsx` (covers calls where `user` isn't passed)
  // - nothing (anonymous)
  try {
    const g: any = globalThis as any;
    const userToken = typeof user?.token === "string" ? user.token : "";
    const bridgedToken = typeof g?.__calceraClerkJwt === "string" ? g.__calceraClerkJwt : "";

    // Prefer the freshest token available. This prevents stale `user.token` values
    // (cached once in a component) from overriding a fresher bridged token.
    const token = pickFreshestToken(userToken, bridgedToken);
    if (token) {
      h.set("Authorization", `Bearer ${token}`);
    }
  } catch {
    // ignore
  }
  return h;
}

function logDebug(resp: Response, label: string) {
  const dbg = resp.headers.get("x-calcera-debug");
  const base = resp.headers.get("x-calcera-backend-base");
  if (dbg || base) {
    console.debug(`[calcera:${label}]`, dbg ?? "<no-debug>", base ? { backend: base } : {});
  }
}

// --- Lightweight request cache & deduplication ---

class RequestCache {
  private store = new Map<string, { expiresAt: number; value: unknown }>();
  get<T>(key: string): T | undefined {
    const entry = this.store.get(key);
    if (!entry) return undefined;
    if (Date.now() >= entry.expiresAt) {
      this.store.delete(key);
      return undefined;
    }
    return entry.value as T;
  }
  set<T>(key: string, value: T, ttlMs: number): void {
    this.store.set(key, { value, expiresAt: Date.now() + Math.max(0, ttlMs) });
  }
  invalidate(key: string): void {
    this.store.delete(key);
  }
  invalidatePattern(prefix: string): void {
    for (const k of this.store.keys()) {
      if (k.startsWith(prefix)) this.store.delete(k);
    }
  }
}

const requestCache = new RequestCache();
const inflightRequests = new Map<string, Promise<unknown>>();

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function parseRetryAfter(headerVal: string | null, attempt: number): number {
  if (!headerVal) {
    const base = Math.pow(2, Math.max(0, attempt - 1)) * 1000; // 1s, 2s, 4s
    const jitter = 100 + Math.floor(Math.random() * 300);
    return base + jitter;
  }
  const asInt = Number(headerVal);
  if (!Number.isNaN(asInt) && asInt >= 0) {
    return asInt * 1000;
  }
  const asDate = Date.parse(headerVal);
  if (!Number.isNaN(asDate)) {
    const delay = asDate - Date.now();
    return delay > 0 ? delay : 0;
  }
  return 1000;
}

async function fetchWithRetries(url: string, init: RequestInit, label: string, maxRetries = 3): Promise<Response> {
  const debugApi = isApiDebugEnabled();
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    if (debugApi && attempt === 1) {
      try {
        const h = new Headers(init.headers || undefined);
        console.log("[DEBUG:api] REQUEST", {
          label,
          url,
          method: init.method || "GET",
          headers: headersToLogObject(h),
        });
      } catch {
        // ignore
      }
    }
    const resp = await fetch(url, init);
    logDebug(resp, label);
    if (debugApi && !resp.ok) {
      try {
        const bodyPreview = await resp.clone().text();
        const headersObj: Record<string, string> = {};
        resp.headers.forEach((value, key) => {
          headersObj[key] = value;
        });
        console.warn("[DEBUG:api] RESPONSE", {
          label,
          url,
          status: resp.status,
          statusText: resp.statusText,
          headers: headersObj,
          body: (bodyPreview || "").slice(0, 4000),
        });
      } catch {
        // ignore
      }
    }
    if (resp.status !== 429) {
      return resp;
    }
    if (attempt === maxRetries) {
      return resp;
    }
    const retryAfter = resp.headers.get("retry-after");
    const delay = parseRetryAfter(retryAfter, attempt);
    console.warn(`[api:${label}] 429 received. Retrying in ${Math.ceil(delay)}ms (attempt ${attempt}/${maxRetries - 1})`);
    await sleep(delay);
  }
  // Should not reach here, but TS needs a return
  return fetch(url, init);
}

export async function fetchWithAuthRetry(
  url: string,
  buildInit: (headers: Headers) => RequestInit,
  label: string,
  user?: UserHeaders,
): Promise<Response> {
  const doFetch = async () => {
    const headers = buildHeaders(user);
    const init = buildInit(headers);
    return fetchWithRetries(url, init, label);
  };

  let resp = await doFetch();
  if (resp.status !== 401) {
    return resp;
  }

  // Force a fresh Clerk token (bridge handles skipCache) then retry once.
  const refreshed = await refreshClerkJwtIfPossible();
  if (refreshed && refreshed.trim()) {
    resp = await doFetch();
  }
  return resp;
}

async function withCache<T>(key: string, ttlMs: number, compute: () => Promise<T>): Promise<T> {
  const cached = requestCache.get<T>(key);
  if (cached !== undefined) return cached;
  const inflight = inflightRequests.get(key) as Promise<T> | undefined;
  if (inflight) return inflight;
  const p = compute()
    .then((value) => {
      requestCache.set<T>(key, value, ttlMs);
      return value;
    })
    .finally(() => {
      inflightRequests.delete(key);
    });
  inflightRequests.set(key, p);
  return p;
}

// Normalizes common list envelope shapes to a flat array
function normalizeArrayResponse<T>(json: any): T[] {
  if (Array.isArray(json)) return json as T[];
  if (json && Array.isArray(json.items)) return json.items as T[];
  if (json && Array.isArray(json.data)) return json.data as T[];
  if (json && Array.isArray(json.tasks)) return json.tasks as T[];
  if (json && Array.isArray(json.notes)) return json.notes as T[];
  if (json && Array.isArray(json.companies)) return json.companies as T[];
  if (json && Array.isArray(json.projects)) return json.projects as T[];
  if (json && Array.isArray(json.results)) return json.results as T[];
  return [] as T[];
}

function isJsonContent(resp: Response): boolean {
  const contentType = resp.headers.get("content-type") || "";
  return contentType.toLowerCase().includes("application/json");
}

async function readApiErrorMessage(resp: Response, label: string): Promise<string> {
  let bodyText = "";
  try {
    bodyText = await resp.clone().text();
  } catch {
    bodyText = "";
  }

  if (!bodyText) {
    return resp.statusText || `Request failed (${label})`;
  }

  try {
    const json = JSON.parse(bodyText);
    const msg =
      (typeof json?.message === "string" && json.message.trim()) ||
      (typeof json?.error === "string" && json.error.trim()) ||
      (typeof json?.detail === "string" && json.detail.trim()) ||
      "";
    const code = typeof json?.code === "string" && json.code.trim() ? json.code.trim() : "";
    if (msg && code) return `${msg} (${code})`;
    if (msg) return msg;
  } catch {
    // ignore
  }

  // Fall back to raw text (trimmed)
  return (bodyText || "").trim().slice(0, 400) || resp.statusText || `Request failed (${label})`;
}

async function throwApiError(resp: Response, label: string): Promise<never> {
  const msg = await readApiErrorMessage(resp, label);
  throw new Error(msg || `Request failed (${label}): ${resp.status}`);
}

const TASK_TYPE_VALUES: TaskType[] = ["Design", "Coordination", "Submittal", "RFI/ASI", "PCR/PCO", "Deliverable"];
const TASK_DISCIPLINE_VALUES: TaskDiscipline[] = [
  "Mechanical",
  "Electrical",
  "Lighting",
  "Plumbing",
  "Technology",
  "Fire Protection",
  "Misc",
];
const TASK_STATUS_VALUES: Task["status"][] = ["pending", "in_progress", "complete", "coordinating"];
const LAST_DISCIPLINE_STORAGE_KEY = "calcera:last-discipline";

function coerceTaskType(value: unknown): TaskType | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const match = TASK_TYPE_VALUES.find((opt) => opt.toLowerCase() === trimmed.toLowerCase());
  return match ?? null;
}

function coerceTaskDiscipline(value: unknown): TaskDiscipline | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const match = TASK_DISCIPLINE_VALUES.find((opt) => opt.toLowerCase() === trimmed.toLowerCase());
  return match ?? null;
}

function coerceTaskStatus(value: unknown): Task["status"] {
  if (typeof value !== "string") return "pending";
  const normalized = value.trim().toLowerCase();
  if (normalized === "todo") return "pending";
  if (normalized === "doing" || normalized === "in-progress") return "in_progress";
  if (normalized === "done") return "complete";
  if (normalized === "coordinating") return "coordinating";
  if (TASK_STATUS_VALUES.some((opt) => opt === normalized)) return normalized as Task["status"];
  return "pending";
}

export function getLastDisciplinePreference(): TaskDiscipline | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(LAST_DISCIPLINE_STORAGE_KEY);
    return coerceTaskDiscipline(raw) ?? null;
  } catch {
    return null;
  }
}

export function setLastDisciplinePreference(value: TaskDiscipline | null | undefined): void {
  if (typeof window === "undefined") return;
  try {
    if (!value) {
      window.localStorage.removeItem(LAST_DISCIPLINE_STORAGE_KEY);
      return;
    }
    const coerced = coerceTaskDiscipline(value);
    if (!coerced) {
      window.localStorage.removeItem(LAST_DISCIPLINE_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(LAST_DISCIPLINE_STORAGE_KEY, coerced);
  } catch {
    // ignore storage failures
  }
}

function randomLocalId(prefix: string): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return `${prefix}:${crypto.randomUUID()}`;
    }
  } catch {
    // ignore
  }
  return `${prefix}:${Math.random().toString(36).slice(2)}`;
}

async function parseJsonSafely<T = any>(resp: Response, label: string): Promise<T> {
  const text = await resp.text();
  try {
    const json = JSON.parse(text);
    return json as T;
  } catch (e: any) {
    console.error(`[api:${label}] JSON parse error`, {
      status: resp.status,
      statusText: resp.statusText,
      contentType: resp.headers.get("content-type"),
      bodyPreview: (text || "").slice(0, 400),
    });
    throw new Error(`Invalid JSON from ${label}`);
  }
}

export async function postChat(body: ChatRequest, user?: UserHeaders): Promise<ChatResponse> {
  console.debug("[api:chat] request", { bodyPreview: JSON.stringify(body).slice(0, 200) });
  const resp = await fetchWithAuthRetry(
    "/api/chat",
    (headers) => ({ method: "POST", headers, body: JSON.stringify(body) }),
    "chat",
    user,
  );
  logDebug(resp, "chat");
  const parsed = await parseJsonSafely<ChatResponse>(resp, "chat");
  console.debug("[api:chat] response", parsed);
  return parsed;
}

export async function getLimitsChat(user?: UserHeaders): Promise<RateLimitStatus> {
  console.debug("[api:limits] request");
  const resp = await fetchWithAuthRetry(
    "/api/limits/chat",
    (headers) => ({ headers }),
    "limits",
    user,
  );
  logDebug(resp, "limits");
  return parseJsonSafely<RateLimitStatus>(resp, "limits");
}

export async function getHistoryThreads(params: { limit?: number } = {}, user?: UserHeaders): Promise<ChatThreadsResponse> {
  const sp = new URLSearchParams();
  if (params.limit) sp.set("limit", String(params.limit));
  const url = "/api/history/threads" + (sp.toString() ? `?${sp.toString()}` : "");
  console.debug("[api:history:threads] request", { url });
  const resp = await fetchWithAuthRetry(
    url,
    (headers) => ({ headers }),
    "history-threads",
    user,
  );
  logDebug(resp, "history-threads");
  return parseJsonSafely<ChatThreadsResponse>(resp, "history-threads");
}

export async function createHistoryThread(
  body: CreateChatThreadRequest = {},
  user?: UserHeaders,
): Promise<CreateChatThreadResponse> {
  console.debug("[api:history:threads:create] request", { bodyPreview: JSON.stringify(body).slice(0, 120) });
  const resp = await fetchWithAuthRetry(
    "/api/history/threads",
    (headers) => ({ method: "POST", headers, body: JSON.stringify(body) }),
    "history-threads-create",
    user,
  );
  logDebug(resp, "history-threads-create");
  return parseJsonSafely<CreateChatThreadResponse>(resp, "history-threads-create");
}

export async function updateHistoryThreadTitle(
  threadId: string,
  title: string,
  user?: UserHeaders,
): Promise<ChatThread | null> {
  if (!threadId) return null;
  const safeTitle = (title || "").trim();
  console.debug("[api:history:threads:update] request", {
    threadId,
    titlePreview: safeTitle.slice(0, 80),
  });
  const resp = await fetchWithAuthRetry(
    `/api/history/threads/${encodeURIComponent(threadId)}`,
    (headers) => ({
      method: "PATCH",
      headers,
      body: JSON.stringify({ title: safeTitle }),
    }),
    "history-threads-update",
    user,
  );
  logDebug(resp, "history-threads-update");
  if (!resp.ok) {
    console.warn("[api:history:threads:update] http error", resp.status, resp.statusText);
    return null;
  }
  try {
    const parsed = await parseJsonSafely<any>(resp, "history-threads-update");
    return (parsed?.thread as ChatThread) ?? null;
  } catch {
    return null;
  }
}

export async function getHistoryMessages(params: { threadId: string; before?: string; limit?: number }, user?: UserHeaders): Promise<ChatMessagesResponse> {
  const sp = new URLSearchParams();
  sp.set("threadId", params.threadId);
  if (params.before) sp.set("before", params.before);
  if (params.limit) sp.set("limit", String(params.limit));
  const url = `/api/history/messages?${sp.toString()}`;
  console.debug("[api:history:messages] request", { url });
  const resp = await fetchWithAuthRetry(
    url,
    (headers) => ({ headers }),
    "history-messages",
    user,
  );
  logDebug(resp, "history-messages");
  return parseJsonSafely<ChatMessagesResponse>(resp, "history-messages");
}

export async function postFeedback(body: any, user?: UserHeaders): Promise<any> {
  const resp = await fetchWithAuthRetry(
    "/api/feedback",
    (headers) => ({ method: "POST", headers, body: JSON.stringify(body) }),
    "feedback",
    user,
  );
  logDebug(resp, "feedback");
  const text = await resp.text();
  try { return JSON.parse(text); } catch { return { status: resp.status, body: text || null }; }
}

export async function postThumbs(
  args: {
    vote: "up" | "down";
    prompt: string;
    userId?: string;
    message_id?: string;
    thread_id?: string;
    gate_policy_v1?: any;
  },
  user?: UserHeaders,
): Promise<any> {
  const payload: any = { vote: args.vote, prompt: args.prompt };
  if (args.userId) payload.userId = args.userId;
  if (args.message_id) payload.message_id = args.message_id;
  if (args.thread_id) payload.thread_id = args.thread_id;
  if (args.gate_policy_v1 !== undefined) payload.gate_policy_v1 = args.gate_policy_v1;
  const resp = await fetchWithAuthRetry(
    "/api/thumbs",
    (headers) => ({ method: "POST", headers, body: JSON.stringify(payload) }),
    "thumbs",
    user,
  );
  logDebug(resp, "thumbs");
  const debug = resp.headers.get("x-calcera-debug") || undefined;
  try {
    const data = await parseJsonSafely<any>(resp, "thumbs");
    return { status: resp.status, ok: resp.ok, debug, data };
  } catch (e: any) {
    return { status: resp.status, ok: resp.ok, debug, error: e?.message };
  }
}

export async function postBetaSignup(body: { email: string; captchaToken?: string }, user?: UserHeaders): Promise<any> {
  const payload: any = { email: (body.email || "").trim() };
  if (body.captchaToken) payload.captchaToken = body.captchaToken;

  const resp = await fetchWithAuthRetry(
    "/api/beta-signup",
    (headers) => ({ method: "POST", headers, body: JSON.stringify(payload) }),
    "beta-signup",
    user,
  );
  logDebug(resp, "beta-signup");
  const text = await resp.text();
  let data: any = {};
  try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
  if (!resp.ok) {
    const message = data?.error || resp.statusText || "Unable to submit";
    throw new Error(message);
  }
  return data;
}

export async function getCurrentUser(user?: UserHeaders): Promise<any | null> {
  console.debug("[api:me] request");
  try {
    const resp = await fetchWithAuthRetry(
      "/api/me",
      (headers) => ({ headers }),
      "me",
      user,
    );
    logDebug(resp, "me");
    if (!resp.ok) {
      console.warn("[api:me] http error", resp.status, resp.statusText);
      return null;
    }
    if (!isJsonContent(resp)) {
      console.warn("[api:me] non-JSON response, returning null");
      return null;
    }
    const json = await parseJsonSafely<any>(resp, "me");
    return json ?? null;
  } catch (e) {
    console.error("[api:me] error", e);
    return null;
  }
}

export async function getMeRbac(user?: UserHeaders): Promise<import("./types").MeRbacResponse | null> {
  console.debug("[api:me/rbac] request");
  try {
    const resp = await fetchWithAuthRetry(
      "/api/me/rbac",
      (headers) => ({ headers }),
      "me-rbac",
      user,
    );
    logDebug(resp, "me-rbac");
    if (!resp.ok) {
      console.warn("[api:me/rbac] http error", resp.status, resp.statusText);
      return null;
    }
    if (!isJsonContent(resp)) {
      console.warn("[api:me/rbac] non-JSON response, returning null");
      return null;
    }
    const json = await parseJsonSafely<import("./types").MeRbacResponse>(resp, "me-rbac");
    return json ?? null;
  } catch (e) {
    console.error("[api:me/rbac] error", e);
    return null;
  }
}

// --- Helpers: task payload sanitation and normalization ---

function isBlank(value: unknown): boolean {
  return value === undefined || value === null || (typeof value === "string" && value.trim() === "");
}

function normalizeDateToIso(dateStr: string): string {
  // Convert YYYY-MM-DD to ISO datetime at local midnight, else pass through
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y, m, d] = dateStr.split("-").map(Number);
    const local = new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
    return local.toISOString();
  }
  return dateStr;
}

function sanitizeTaskPayload<T extends Partial<CreateTaskInput | UpdateTaskInput>>(input: T): any {
  const out: Record<string, any> = {};
  let taskTypeSeen = false;
  let pendingTaskType: string | null = null;
  const allowExplicitNull = new Set(["project_id", "task_type", "completed_at"]);
  for (const [key, value] of Object.entries(input as Record<string, any>)) {
    if (key === "labels") {
      if (Array.isArray(value)) {
        const filtered = value.filter((v) => typeof v === "string" && v.trim() !== "");
        if (filtered.length > 0) out.labels = filtered;
      }
      continue;
    }
    if (key === "due_date") {
      if (!isBlank(value) && typeof value === "string") {
        out.due_date = normalizeDateToIso(value);
      }
      continue;
    }
    if (key === "completed_at") {
      if (value === null) {
        out.completed_at = null;
      } else if (!isBlank(value) && typeof value === "string") {
        out.completed_at = normalizeDateToIso(value);
      }
      continue;
    }
    if (key === "task_type") {
      taskTypeSeen = true;
      if (value === null || value === undefined) {
        pendingTaskType = null;
      } else if (typeof value === "string") {
        const trimmed = value.trim();
        pendingTaskType = trimmed ? trimmed : null;
      }
      continue;
    }
    if (isBlank(value)) {
      if (value === null && allowExplicitNull.has(key)) {
        out[key] = null;
      }
      continue;
    }
    out[key] = value;
  }
  if (taskTypeSeen) {
    out.task_type = pendingTaskType;
    if (pendingTaskType) {
      out.type = pendingTaskType;
      if (!("labels" in out) || !Array.isArray(out.labels)) {
        out.labels = [pendingTaskType];
      }
    } else {
    }
  }
  return out;
}

function sanitizeProjectPayload(input: CreateProjectInput): Record<string, any> {
  const out: Record<string, any> = {};
  if (typeof input.project_name === "string") {
    const trimmed = input.project_name.trim();
    if (trimmed) out.project_name = trimmed;
  }
  if (typeof input.client_name === "string") {
    const trimmed = input.client_name.trim();
    if (trimmed) out.client_name = trimmed;
  }
  if (typeof input.city === "string") {
    const trimmed = input.city.trim();
    if (trimmed) out.city = trimmed.toUpperCase();
  }
  if (typeof input.state === "string") {
    const trimmed = input.state.trim().toUpperCase();
    if (trimmed) out.state = trimmed;
  }
  if (typeof input.company_id === "string") {
    const trimmed = input.company_id.trim();
    if (trimmed) out.company_id = trimmed;
  }
  return out;
}

function sanitizeProjectUpdatePayload(input: UpdateProjectInput): Record<string, any> {
  const out: Record<string, any> = {};
  if (typeof input.project_name === "string") {
    const trimmed = input.project_name.trim();
    if (trimmed) out.project_name = trimmed;
  } else if (input.project_name === null) {
    out.project_name = null;
  }
  if (typeof input.client_name === "string") {
    const trimmed = input.client_name.trim();
    if (trimmed) out.client_name = trimmed;
  } else if (input.client_name === null) {
    out.client_name = null;
  }
  if (typeof input.city === "string") {
    const trimmed = input.city.trim().toUpperCase();
    if (trimmed) out.city = trimmed;
  } else if (input.city === null) {
    out.city = null;
  }
  if (typeof input.state === "string") {
    const trimmed = input.state.trim().toUpperCase();
    if (trimmed) out.state = trimmed;
  } else if (input.state === null) {
    out.state = null;
  }
  return out;
}

function mapStatusForAlternateScheme(status: any): any {
  // Map between legacy and new forms
  if (status === "todo") return "pending";
  if (status === "doing") return "in-progress";
  if (status === "done") return "complete";
  if (status === "in_progress") return "in-progress";
  if (status === "in-progress") return "in_progress";
  if (status === "pending") return "todo";
  if (status === "complete") return "done";
  if (status === "coordinating") return "coordinating";
  return status;
}

// --- Tasks API (via CF Worker proxy) ---

function normalizeTaskKeys(raw: any): Task {
  // Expect ONLY snake_case from backend (DB-originated data)
  const id = raw?.id ?? String(Math.random());
  const user_id = raw?.user_id ?? "";
  const title = raw?.title ?? "";
  const description = raw?.description ?? null;
  const status = coerceTaskStatus(raw?.status);
  const due_date = raw?.due_date ?? null;
  const priority = raw?.priority ?? null;
  const discipline = coerceTaskDiscipline(raw?.discipline);
  const labels = Array.isArray(raw?.labels) ? raw.labels : raw?.labels == null ? null : [];
  const inferredTypeSource = raw?.task_type ?? raw?.type ?? (labels && labels.length > 0 ? labels[0] : null);
  const task_type = coerceTaskType(inferredTypeSource);
  const parent_id = raw?.parent_id ?? null;
  const project_name = raw?.project_name ?? null;
  const project_id = raw?.project_id ?? null;
  const company_id = raw?.company_id ?? null;
  const created_by = raw?.created_by ?? null;
  const assigned_to = raw?.assigned_to ?? null;
  const completed_at = raw?.completed_at ?? raw?.completedAt ?? null;
  const access = raw?.access ?? undefined;
  const created_at = raw?.created_at ?? new Date().toISOString();
  const updated_at = raw?.updated_at ?? created_at;
  return {
    id,
    user_id,
    title,
    description,
    status,
    due_date,
    priority,
    discipline,
    labels,
    task_type,
    parent_id,
    project_name,
    project_id,
    company_id,
    created_by,
    assigned_to,
    completed_at,
    access,
    created_at,
    updated_at,
  } as Task;
}

function normalizeNoteKeys(raw: any): Note {
  // Notes may arrive in different shapes (snake_case from DB, camelCase from some services).
  // Normalize to the client-local `Note` contract so ownership/share UI works reliably.
  const id = raw?.id ?? String(Math.random());

  const user_id =
    raw?.user_id ??
    raw?.userId ??
    raw?.owner_id ??
    raw?.ownerId ??
    raw?.created_by ??
    raw?.createdBy ??
    "";

  const title = raw?.title ?? null;
  const content = raw?.content ?? "";
  const tags = Array.isArray(raw?.tags) ? raw.tags : raw?.tags == null ? null : [];

  const project_id = raw?.project_id ?? raw?.projectId ?? null;
  const project_name = raw?.project_name ?? raw?.projectName ?? null;
  const company_id = raw?.company_id ?? raw?.companyId ?? null;

  // Access info (optional)
  let access = raw?.access ?? undefined;
  if (!access) {
    const kind = raw?.access_kind ?? raw?.accessKind ?? raw?.kind ?? undefined;
    const permission = raw?.access_permission ?? raw?.accessPermission ?? raw?.permission ?? undefined;
    const shared_by = raw?.shared_by ?? raw?.sharedBy ?? undefined;
    const shared_at = raw?.shared_at ?? raw?.sharedAt ?? undefined;
    const share_id = raw?.share_id ?? raw?.shareId ?? undefined;
    if (kind) {
      access = {
        kind,
        ...(permission ? { permission } : {}),
        ...(shared_by ? { shared_by } : {}),
        ...(shared_at ? { shared_at } : {}),
        ...(share_id ? { share_id } : {}),
      };
    }
  }

  const created_at = raw?.created_at ?? raw?.createdAt ?? new Date().toISOString();
  const updated_at = raw?.updated_at ?? raw?.updatedAt ?? created_at;

  return {
    id,
    user_id,
    title,
    content,
    tags,
    company_id,
    project_id,
    project_name,
    access,
    created_at,
    updated_at,
  } as Note;
}

function normalizeCompany(raw: any): CompanySummary {
  return {
    id: raw?.id ?? "",
    name: raw?.name ?? "",
    email: raw?.email ?? null,
    phone: raw?.phone ?? null,
    website: raw?.website ?? null,
    address_line1: raw?.address_line1 ?? null,
    address_line2: raw?.address_line2 ?? null,
    city: raw?.city ?? null,
    state: raw?.state ?? null,
    postal_code: raw?.postal_code ?? null,
    country: raw?.country ?? null,
    created_at: raw?.created_at ?? null,
    updated_at: raw?.updated_at ?? null,
  };
}

function normalizeProject(raw: any): ProjectSummary {
  return {
    id: raw?.id ?? "",
    company_id: raw?.company_id ?? null,
    project_name: raw?.project_name ?? raw?.name ?? "",
    client_name: raw?.client_name ?? null,
    city: raw?.city ?? null,
    state: raw?.state ?? null,
    capabilities:
      raw?.capabilities && typeof raw.capabilities === "object"
        ? (raw.capabilities as any)
        : null,
    created_at: raw?.created_at ?? null,
    updated_at: raw?.updated_at ?? null,
  };
}

function normalizeTrackerTask(raw: any): TrackerTaskState {
  const id = typeof raw?.id === "string" && raw.id.trim() ? raw.id : randomLocalId("tracker-task");
  const text = typeof raw?.text === "string" ? raw.text : "";
  return {
    id,
    text,
    done: Boolean(raw?.done),
    hint: typeof raw?.hint === "string" && raw.hint.trim() ? raw.hint : null,
  };
}

function normalizeOwnerRequirement(raw: any): OwnerRequirementState {
  const id = typeof raw?.id === "string" && raw.id.trim() ? raw.id : randomLocalId("owner-req");
  const text = typeof raw?.text === "string" ? raw.text : "";
  const source_user_id =
    typeof raw?.source_user_id === "string" && raw.source_user_id.trim() ? raw.source_user_id : null;
  const source_name = typeof raw?.source_name === "string" && raw.source_name.trim() ? raw.source_name : null;
  const created_at = typeof raw?.created_at === "string" && raw.created_at.trim() ? raw.created_at : null;
  const updated_at = typeof raw?.updated_at === "string" && raw.updated_at.trim() ? raw.updated_at : null;
  return { id, text, done: Boolean(raw?.done), source_user_id, source_name, created_at, updated_at };
}

function normalizeOwnerRequirementDoc(raw: any): OwnerRequirementDoc {
  return {
    id: typeof raw?.id === "string" ? raw.id : "",
    project_id: typeof raw?.project_id === "string" ? raw.project_id : "",
    discipline: typeof raw?.discipline === "string" ? raw.discipline : "ALL",
    items: Array.isArray(raw?.items) ? raw.items.map(normalizeOwnerRequirement) : [],
    created_by: raw?.created_by ?? null,
    updated_by: raw?.updated_by ?? null,
    created_at: typeof raw?.created_at === "string" ? raw.created_at : "",
    updated_at: typeof raw?.updated_at === "string" ? raw.updated_at : "",
  };
}

function normalizeOwnerRequirementTemplate(raw: any): OwnerRequirementTemplate {
  return {
    id: typeof raw?.id === "string" ? raw.id : "",
    company_id: typeof raw?.company_id === "string" ? raw.company_id : "",
    template_level: "location",
    city: raw?.city ?? null,
    state: raw?.state ?? null,
    discipline: typeof raw?.discipline === "string" ? raw.discipline : "ALL",
    items: Array.isArray(raw?.items) ? raw.items.map(normalizeOwnerRequirement) : [],
    created_by: raw?.created_by ?? null,
    updated_by: raw?.updated_by ?? null,
    created_at: typeof raw?.created_at === "string" ? raw.created_at : "",
    updated_at: typeof raw?.updated_at === "string" ? raw.updated_at : "",
  };
}

function normalizeTrackerMilestone(raw: any): TrackerMilestoneState {
  const percent = typeof raw?.percent === "number" ? raw.percent : Number(raw?.percent) || 0;
  const title = typeof raw?.title === "string" ? raw.title : `${percent}%`;
  const tasks = Array.isArray(raw?.tasks) ? raw.tasks.map(normalizeTrackerTask) : [];
  return { percent, title, tasks };
}

function normalizeTrackerDiscipline(raw: any): TrackerDisciplineState {
  const discipline = typeof raw?.discipline === "string" ? raw.discipline : "General";
  const milestones = Array.isArray(raw?.milestones) ? raw.milestones.map(normalizeTrackerMilestone) : [];
  const updated_at = raw?.updated_at ?? null;
  return { discipline, milestones, updated_at };
}

function normalizeProjectTracker(raw: any, projectId?: string): ProjectTrackerState {
  const project_id = raw?.project_id ?? projectId ?? "";
  const updated_at = raw?.updated_at ?? null;
  const progress_state = raw?.progress_state === "active" || raw?.progress_state === "read_only" ? raw.progress_state : undefined;
  const read_only =
    typeof raw?.read_only === "boolean"
      ? raw.read_only
      : progress_state
        ? progress_state !== "active"
        : undefined;
  const updated_by = raw?.updated_by ?? null;
  const disciplines = Array.isArray(raw?.disciplines) ? raw.disciplines.map(normalizeTrackerDiscipline) : [];
  const owner_requirements = Array.isArray(raw?.owner_requirements) ? raw.owner_requirements.map(normalizeOwnerRequirement) : [];
  return { project_id, updated_at, progress_state, read_only, updated_by, disciplines, owner_requirements };
}

export async function listTasks(user?: UserHeaders): Promise<Task[]> {
  console.debug("[api:tasks:list] request");
  const key = "cache:tasks:list";
  const ttlMs = 30_000;
  return withCache<Task[]>(key, ttlMs, async () => {
    let logged = false;
    const resp = await fetchWithAuthRetry(
      "/api/tasks",
      (headers) => {
        if (!logged) {
          logged = true;
          const headersObj = headersToLogObject(headers);
          console.log("[DEBUG:tasks:list] REQUEST", {
            url: "/api/tasks",
            method: "GET",
            headers: headersObj,
            userParam: user,
          });
        }
        return { headers };
      },
      "tasks:list",
      user,
    );
    const respHeadersObj: Record<string, string> = {};
    resp.headers.forEach((value, key) => { respHeadersObj[key] = value; });
    let bodyText = "";
    try {
      bodyText = await resp.clone().text();
    } catch {}
    console.log("[DEBUG:tasks:list] RESPONSE", {
      status: resp.status,
      statusText: resp.statusText,
      headers: respHeadersObj,
      body: bodyText.slice(0, 2000),
    });
    if (!resp.ok) {
      console.error("[api:tasks:list] http error", resp.status, resp.statusText);
      await throwApiError(resp, "tasks:list");
    }
    const json = await parseJsonSafely<any>(resp, "tasks:list");
    const items = normalizeArrayResponse<any>(json).map(normalizeTaskKeys);
    return items as Task[];
  });
}

export async function getTaskById(id: string, user?: UserHeaders): Promise<Task | null> {
  console.log("[source-pill:tasks:get] requesting task", { id });
  const resp = await fetchWithAuthRetry(
    `/api/tasks/${encodeURIComponent(id)}`,
    (headers) => ({ headers }),
    "tasks:get",
    user,
  );
  console.log("[source-pill:tasks:get] response", { id, status: resp.status, ok: resp.ok });
  if (resp.status === 404) {
    console.warn("[source-pill:tasks:get] 404 — task not found for id:", id);
    return null;
  }
  if (!resp.ok) await throwApiError(resp, "tasks:get");
  const json = await parseJsonSafely<any>(resp, "tasks:get");
  const raw = json?.task ?? json;
  console.log("[source-pill:tasks:get] parsed", { id, hasTask: !!raw, keys: raw ? Object.keys(raw) : [] });
  return raw ? (normalizeTaskKeys(raw) as Task) : null;
}

export async function createTask(input: CreateTaskInput, user?: UserHeaders): Promise<Task> {
  console.debug("[api:tasks:create] request", { input });
  // Backend expects snake_case; sanitize payload and normalize dates
  const primary = sanitizeTaskPayload(input);

  const doPost = async () =>
    fetchWithAuthRetry(
      "/api/tasks",
      (headers) => ({ method: "POST", headers, body: JSON.stringify(primary) }),
      "tasks:create",
      user,
    );

  let resp = await doPost();
  logDebug(resp, "tasks:create");
  if (!resp.ok) {
    let body = "";
    try {
      body = await resp.text();
    } catch {}

    // Guarded single retry with alternate status scheme only on 400 invalid request
    if (resp.status === 400 && /invalid request/i.test(body || "") && (primary as any).status) {
      const alt = { ...primary, status: mapStatusForAlternateScheme((primary as any).status) };
      if (alt.status !== (primary as any).status) {
        const retry = await fetchWithAuthRetry(
          "/api/tasks",
          (headers) => ({ method: "POST", headers, body: JSON.stringify(alt) }),
          "tasks:create",
          user,
        );
        logDebug(retry, "tasks:create");
        if (!retry.ok) {
          console.error("[api:tasks:create] http error(retry)", retry.status, retry.statusText);
          await throwApiError(retry, "tasks:create");
        }
        const json2 = await parseJsonSafely<any>(retry, "tasks:create");
        const obj2 = json2.task || json2;
        requestCache.invalidate("cache:tasks:list");
        return normalizeTaskKeys(obj2);
      }
    }
    console.error("[api:tasks:create] http error", resp.status, resp.statusText, (body || "").slice(0, 200));
    await throwApiError(resp, "tasks:create");
  }
  const json = await parseJsonSafely<any>(resp, "tasks:create");
  const obj = json.task || json;
  requestCache.invalidate("cache:tasks:list");
  return normalizeTaskKeys(obj);
}

export async function updateTask(id: string, changes: UpdateTaskInput, user?: UserHeaders): Promise<Task> {
  console.debug("[api:tasks:update] request", { id, changes });
  // Backend expects snake_case; sanitize payload and normalize dates
  const primary = sanitizeTaskPayload(changes);
  const resp = await fetchWithAuthRetry(
    `/api/tasks/${encodeURIComponent(id)}`,
    (headers) => ({ method: "PATCH", headers, body: JSON.stringify(primary) }),
    "tasks:update",
    user,
  );
  logDebug(resp, "tasks:update");
  if (!resp.ok) {
    let body = "";
    try { body = await resp.text(); } catch {}
    if (resp.status === 400 && /invalid request/i.test(body || "") && (primary as any).status) {
      const alt = { ...primary, status: mapStatusForAlternateScheme((primary as any).status) };
      if (alt.status !== (primary as any).status) {
        const retry = await fetchWithAuthRetry(
          `/api/tasks/${encodeURIComponent(id)}`,
          (headers) => ({ method: "PATCH", headers, body: JSON.stringify(alt) }),
          "tasks:update",
          user,
        );
        logDebug(retry, "tasks:update");
        if (!retry.ok) {
          console.error("[api:tasks:update] http error(retry)", retry.status, retry.statusText);
          await throwApiError(retry, "tasks:update");
        }
        const json2 = await parseJsonSafely<any>(retry, "tasks:update");
        const obj2 = json2.task || json2;
        requestCache.invalidate("cache:tasks:list");
        return normalizeTaskKeys(obj2);
      }
    }
    console.error("[api:tasks:update] http error", resp.status, resp.statusText, (body || "").slice(0, 200));
    await throwApiError(resp, "tasks:update");
  }
  const json = await parseJsonSafely<any>(resp, "tasks:update");
  const obj = json.task || json;
  requestCache.invalidate("cache:tasks:list");
  return normalizeTaskKeys(obj);
}

export async function deleteTask(id: string, user?: UserHeaders): Promise<{ ok: true }> {
  console.debug("[api:tasks:delete] request", { id });
  const resp = await fetchWithAuthRetry(
    `/api/tasks/${encodeURIComponent(id)}`,
    (headers) => ({ method: "DELETE", headers }),
    "tasks:delete",
    user,
  );
  logDebug(resp, "tasks:delete");
  if (!resp.ok) {
    console.error("[api:tasks:delete] http error", resp.status, resp.statusText);
    await throwApiError(resp, "tasks:delete");
  }
  const res = await parseJsonSafely<{ ok: true }>(resp, "tasks:delete");
  requestCache.invalidate("cache:tasks:list");
  return res;
}

// --- Companies & Projects API ---

export async function listCompanies(user?: UserHeaders): Promise<CompanySummary[]> {
  console.debug("[api:companies:list] request");
  const key = "cache:companies:list";
  const ttlMs = 30_000;
  return withCache<CompanySummary[]>(key, ttlMs, async () => {
    const resp = await fetchWithAuthRetry(
      "/api/companies",
      (headers) => ({ headers }),
      "companies:list",
      user,
    );
    if (!resp.ok) {
      console.error("[api:companies:list] http error", resp.status, resp.statusText);
      await throwApiError(resp, "companies:list");
    }
    if (!isJsonContent(resp)) {
      throw new Error("companies:list returned non-JSON response");
    }
    const json = await parseJsonSafely<any>(resp, "companies:list");
    return normalizeArrayResponse<any>(json).map(normalizeCompany);
  });
}

export async function listProjects(params: { companyId?: string } = {}, user?: UserHeaders): Promise<ProjectSummary[]> {
  const companyId = params.companyId;
  console.debug("[api:projects:list] request", { companyId });
  const key = `cache:projects:list:${companyId ?? "all"}`;
  const ttlMs = 30_000;
  return withCache<ProjectSummary[]>(key, ttlMs, async () => {
    try {
      const sp = new URLSearchParams();
      sp.set("include_misc", "true");
      if (companyId) {
        sp.set("company_id", companyId);
      }
      const url = `/api/projects?${sp.toString()}`;
      const resp = await fetchWithAuthRetry(
        url,
        (headers) => ({ headers }),
        "projects:list",
        user,
      );
      if (!resp.ok) {
        if (resp.status !== 404) {
          console.warn("[api:projects:list] http error", resp.status, resp.statusText);
        }
        return [];
      }
      if (!isJsonContent(resp)) {
        console.warn("[api:projects:list] non-JSON response, returning empty list");
        return [];
      }
      const json = await parseJsonSafely<any>(resp, "projects:list");
      return normalizeArrayResponse<any>(json).map(normalizeProject);
    } catch (e) {
      console.error("[api:projects:list] error", e);
      return [];
    }
  });
}

export async function listCompanyMembers(companyId: string, user?: UserHeaders): Promise<CompanyMember[]> {
  if (!companyId) return [];
  console.debug("[api:companies:members:list] request", { companyId });
  const key = `cache:companies:members:${companyId}`;
  const ttlMs = 30_000;
  return withCache<CompanyMember[]>(key, ttlMs, async () => {
    const resp = await fetchWithAuthRetry(
      `/api/companies/${encodeURIComponent(companyId)}/members`,
      (headers) => ({ method: "GET", headers }),
      "companies:members:list",
      user,
    );
    if (!resp.ok) {
      console.error("[api:companies:members:list] http error", resp.status, resp.statusText);
      await throwApiError(resp, "companies:members:list");
    }
    const json = await parseJsonSafely<any>(resp, "companies:members:list");
    return Array.isArray(json?.members) ? (json.members as CompanyMember[]) : [];
  });
}

export async function listProjectMembers(projectId: string, user?: UserHeaders): Promise<ProjectMember[]> {
  if (!projectId) return [];
  console.debug("[api:projects:members:list] request", { projectId });
  const key = `cache:projects:members:${projectId}`;
  const ttlMs = 30_000;
  return withCache<ProjectMember[]>(key, ttlMs, async () => {
    try {
      const resp = await fetchWithAuthRetry(
        `/api/projects/${encodeURIComponent(projectId)}/members`,
        (headers) => ({ method: "GET", headers }),
        "projects:members:list",
        user,
      );
      if (!resp.ok) {
        console.warn("[api:projects:members:list] http error", resp.status, resp.statusText);
        return [];
      }
      const json = await parseJsonSafely<any>(resp, "projects:members:list");
      return Array.isArray(json?.members) ? (json.members as ProjectMember[]) : [];
    } catch (e) {
      console.error("[api:projects:members:list] error", e);
      return [];
    }
  });
}

export async function listProjectPrimaries(projectId: string, user?: UserHeaders): Promise<ProjectPrimary[]> {
  if (!projectId) return [];
  console.debug("[api:projects:primaries:list] request", { projectId });
  const key = `cache:projects:primaries:${projectId}`;
  const ttlMs = 30_000;
  return withCache<ProjectPrimary[]>(key, ttlMs, async () => {
    try {
      const resp = await fetchWithAuthRetry(
        `/api/projects/${encodeURIComponent(projectId)}/members`,
        (headers) => ({ method: "GET", headers }),
        "projects:primaries:list",
        user,
      );
      if (!resp.ok) {
        console.warn("[api:projects:primaries:list] http error", resp.status, resp.statusText);
        return [];
      }
      const json = await parseJsonSafely<any>(resp, "projects:primaries:list");
      return Array.isArray(json?.primaries) ? (json.primaries as ProjectPrimary[]) : [];
    } catch (e) {
      console.error("[api:projects:primaries:list] error", e);
      return [];
    }
  });
}

export async function addProjectMember(
  projectId: string,
  input: { user_id: string; project_role_template: string },
  user?: UserHeaders,
): Promise<{ ok: true }> {
  if (!projectId) throw new Error("projectId is required");
  if (!input?.user_id) throw new Error("user_id is required");
  if (!input?.project_role_template) throw new Error("project_role_template is required");
  console.debug("[api:projects:members:add] request", {
    projectId,
    user_id: input.user_id,
    project_role_template: input.project_role_template,
  });
  const payload: any = {
    user_id: input.user_id,
    project_role_template: String(input.project_role_template).trim(),
  };
  const resp = await fetchWithAuthRetry(
    `/api/projects/${encodeURIComponent(projectId)}/members`,
    (headers) => ({
      method: "POST",
      headers,
      body: JSON.stringify({
        ...payload,
      }),
    }),
    "projects:members:add",
    user,
  );
  const bodyText = await resp.text();
  if (!resp.ok) {
    console.error(
      "[api:projects:members:add] http error",
      resp.status,
      resp.statusText,
      bodyText.slice(0, 200),
    );
    throw new Error(
      bodyText ? bodyText.slice(0, 200) : `Failed to add member: ${resp.status} ${resp.statusText}`,
    );
  }
  requestCache.invalidate(`cache:projects:members:${projectId}`);
  requestCache.invalidate(`cache:projects:primaries:${projectId}`);
  return { ok: true };
}

export async function removeProjectMember(projectId: string, userId: string, user?: UserHeaders): Promise<{ ok: true }> {
  if (!projectId) throw new Error("projectId is required");
  if (!userId) throw new Error("userId is required");
  console.debug("[api:projects:members:remove] request", { projectId, userId });
  const resp = await fetchWithAuthRetry(
    `/api/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(userId)}`,
    (headers) => ({ method: "DELETE", headers }),
    "projects:members:remove",
    user,
  );
  const bodyText = await resp.text();
  if (!resp.ok) {
    console.error(
      "[api:projects:members:remove] http error",
      resp.status,
      resp.statusText,
      bodyText.slice(0, 200),
    );
    throw new Error(bodyText ? bodyText.slice(0, 200) : `Failed to remove member: ${resp.status} ${resp.statusText}`);
  }
  requestCache.invalidate(`cache:projects:members:${projectId}`);
  requestCache.invalidate(`cache:projects:primaries:${projectId}`);
  return { ok: true };
}

export async function createProject(input: CreateProjectInput, user?: UserHeaders): Promise<ProjectSummary> {
  console.debug("[api:projects:create] request", { input });
  const payload = sanitizeProjectPayload(input);
  if (!payload.project_name) {
    throw new Error("Project name is required");
  }
  if (!payload.client_name) {
    throw new Error("Client name is required");
  }
  if (!payload.city) {
    throw new Error("City is required");
  }
  if (!payload.state) {
    throw new Error("State is required (use a two-letter US code)");
  }
  if (!payload.company_id) {
    throw new Error("Company is required");
  }
  const resp = await fetchWithAuthRetry(
    "/api/projects",
    (headers) => ({ method: "POST", headers, body: JSON.stringify(payload) }),
    "projects:create",
    user,
  );
  logDebug(resp, "projects:create");
  if (!resp.ok) {
    let text = "";
    try {
      text = await resp.text();
    } catch {}
    console.error("[api:projects:create] http error", resp.status, resp.statusText, text.slice(0, 200));
    await throwApiError(resp, "projects:create");
  }
  const json = await parseJsonSafely<any>(resp, "projects:create");
  const obj = json.project || json;
  requestCache.invalidatePattern("cache:projects:list");
  const companyId = payload.company_id ?? null;
  if (companyId) {
    requestCache.invalidate(`cache:projects:list:${companyId}`);
  }
  return normalizeProject(obj);
}

export async function updateProject(
  projectId: string,
  input: UpdateProjectInput,
  user?: UserHeaders,
): Promise<ProjectSummary> {
  if (!projectId) {
    throw new Error("projectId is required");
  }
  const payload = sanitizeProjectUpdatePayload(input);
  if (Object.keys(payload).length === 0) {
    throw new Error("No changes to update");
  }
  console.debug("[api:projects:update] request", { projectId, payload });
  const resp = await fetchWithAuthRetry(
    `/api/projects/${encodeURIComponent(projectId)}`,
    (headers) => ({ method: "PATCH", headers, body: JSON.stringify(payload) }),
    "projects:update",
    user,
  );
  logDebug(resp, "projects:update");
  if (!resp.ok) {
    let text = "";
    try {
      text = await resp.text();
    } catch {}
    console.error("[api:projects:update] http error", resp.status, resp.statusText, text.slice(0, 200));
    await throwApiError(resp, "projects:update");
  }
  const json = await parseJsonSafely<any>(resp, "projects:update");
  const obj = json.project || json;
  try {
    console.debug("[api:projects:update] response body", obj);
  } catch {
    // ignore console errors
  }
  requestCache.invalidatePattern("cache:projects:list");
  const companyId = obj?.company_id ?? null;
  if (companyId) {
    requestCache.invalidate(`cache:projects:list:${companyId}`);
  }
  return normalizeProject(obj);
}

export async function deleteProject(projectId: string, user?: UserHeaders): Promise<{ ok: true }> {
  if (!projectId) throw new Error("projectId is required");
  console.debug("[api:projects:delete] request", { projectId });
  const resp = await fetchWithAuthRetry(
    `/api/projects/${encodeURIComponent(projectId)}`,
    (headers) => ({ method: "DELETE", headers }),
    "projects:delete",
    user,
  );
  logDebug(resp, "projects:delete");
  if (!resp.ok) {
    console.error("[api:projects:delete] http error", resp.status, resp.statusText);
    await throwApiError(resp, "projects:delete");
  }
  const res = await parseJsonSafely<{ ok: true }>(resp, "projects:delete");
  requestCache.invalidatePattern("cache:projects:list");
  return res;
}

export async function getProjectTracker(projectId: string, user?: UserHeaders): Promise<ProjectTrackerState | null> {
  if (!projectId) return null;
  console.debug("[api:projects:tracker:get] request", { projectId });
  const key = `cache:project-tracker:${projectId}`;
  const ttlMs = 15_000;
  return withCache<ProjectTrackerState | null>(key, ttlMs, async () => {
    try {
      const resp = await fetchWithAuthRetry(
        `/api/projects/${encodeURIComponent(projectId)}/tracker`,
        (headers) => ({ method: "GET", headers }),
        "projects:tracker:get",
        user,
      );
      if (resp.status === 404) {
        return null;
      }
      if (!resp.ok) {
        console.warn("[api:projects:tracker:get] http error", resp.status, resp.statusText);
        return null;
      }
      if (!isJsonContent(resp)) {
        console.warn("[api:projects:tracker:get] non-JSON response, treating as no tracker");
        return null;
      }
      const json = await parseJsonSafely<any>(resp, "projects:tracker:get");
      const payload = json?.tracker ?? json?.data ?? json;
      return normalizeProjectTracker(payload, projectId);
    } catch (e) {
      console.error("[api:projects:tracker:get] error", e);
      return null;
    }
  });
}

export async function saveProjectTracker(
  projectId: string,
  tracker: ProjectTrackerState,
  user?: UserHeaders,
): Promise<ProjectTrackerState> {
  if (!projectId) throw new Error("projectId is required");
  console.debug("[api:projects:tracker:save] request", { projectId });
  const body = JSON.stringify({ tracker });
  const resp = await fetchWithAuthRetry(
    `/api/projects/${encodeURIComponent(projectId)}/tracker`,
    (headers) => ({ method: "PUT", headers, body }),
    "projects:tracker:save",
    user,
  );
  logDebug(resp, "projects:tracker:save");
  if (!resp.ok) {
    let text = "";
    try {
      text = await resp.text();
    } catch {}
    console.error("[api:projects:tracker:save] http error", resp.status, resp.statusText, text.slice(0, 200));
    throw new Error(`Failed to save project tracker: ${resp.status} ${resp.statusText}`);
  }
  const json = await parseJsonSafely<any>(resp, "projects:tracker:save");
  const payload = json?.tracker ?? json?.data ?? json;
  requestCache.invalidate(`cache:project-tracker:${projectId}`);
  return normalizeProjectTracker(payload, projectId);
}

export async function toggleTrackerLock(
  projectId: string,
  locked: boolean,
  user?: UserHeaders,
): Promise<ProjectTrackerState> {
  if (!projectId) throw new Error("projectId is required");
  console.debug("[api:projects:tracker:lock] request", { projectId, locked });
  const body = JSON.stringify({ locked });
  const resp = await fetchWithAuthRetry(
    `/api/projects/${encodeURIComponent(projectId)}/tracker/lock`,
    (headers) => ({ method: "POST", headers, body }),
    "projects:tracker:lock",
    user,
  );
  if (!resp.ok) {
    let text = "";
    try {
      text = await resp.text();
    } catch {}
    console.error("[api:projects:tracker:lock] http error", resp.status, resp.statusText, text.slice(0, 200));
    throw new Error(`Failed to toggle tracker lock: ${resp.status} ${resp.statusText}`);
  }
  const json = await parseJsonSafely<any>(resp, "projects:tracker:lock");
  const payload = json?.tracker ?? json?.data ?? json;
  requestCache.invalidate(`cache:project-tracker:${projectId}`);
  return normalizeProjectTracker(payload, projectId);
}

export interface TrackerProgressUpdate {
  discipline: string;
  milestonePercent: number;
  taskId: string;
  done: boolean;
  updated_at?: string | null;
}

export async function updateTrackerProgress(
  projectId: string,
  update: TrackerProgressUpdate,
  user?: UserHeaders,
): Promise<ProjectTrackerState> {
  if (!projectId) throw new Error("projectId is required");
  console.debug("[api:projects:tracker:progress] request", { projectId, ...update });
  const body = JSON.stringify(update);
  const resp = await fetchWithAuthRetry(
    `/api/projects/${encodeURIComponent(projectId)}/tracker/progress`,
    (headers) => ({ method: "PATCH", headers, body }),
    "projects:tracker:progress",
    user,
  );
  logDebug(resp, "projects:tracker:progress");
  if (!resp.ok) {
    let errorMessage = `${resp.status} ${resp.statusText}`;
    try {
      const text = await resp.text();
      const parsed = text ? JSON.parse(text) : null;
      if (parsed?.error) errorMessage = parsed.error;
    } catch {}
    console.error("[api:projects:tracker:progress] http error", resp.status, resp.statusText);
    const err = new Error(`Failed to update tracker progress: ${errorMessage}`);
    (err as any).status = resp.status;
    throw err;
  }
  const json = await parseJsonSafely<any>(resp, "projects:tracker:progress");
  const payload = json?.tracker ?? json?.data ?? json;
  requestCache.invalidate(`cache:project-tracker:${projectId}`);
  return normalizeProjectTracker(payload, projectId);
}

// --- Company Tracker Template Disciplines API ---

export async function listCompanyTrackerTemplates(
  companyId: string,
  user?: UserHeaders,
): Promise<{ id: string; template_name: string; discipline: string; updated_at: string }[]> {
  if (!companyId) return [];
  console.debug("[api:tracker-templates:list] request", { companyId });
  try {
    const resp = await fetchWithAuthRetry(
      `/api/companies/${encodeURIComponent(companyId)}/tracker-templates`,
      (headers) => ({ method: "GET", headers }),
      "tracker-templates:list",
      user,
    );
    if (!resp.ok) {
      console.warn("[api:tracker-templates:list] http error", resp.status, resp.statusText);
      return [];
    }
    if (!isJsonContent(resp)) return [];
    const json = await parseJsonSafely<any>(resp, "tracker-templates:list");
    return json?.templates ?? [];
  } catch (e) {
    console.error("[api:tracker-templates:list] error", e);
    return [];
  }
}

export async function getCompanyTrackerTemplateById(
  companyId: string,
  templateId: string,
  user?: UserHeaders,
): Promise<{ id: string; company_id: string; template_name: string; discipline: string; milestones: any[]; updated_at: string; updated_by: string | null } | null> {
  if (!companyId || !templateId) return null;
  console.debug("[api:tracker-templates:get] request", { companyId, templateId });
  try {
    const resp = await fetchWithAuthRetry(
      `/api/companies/${encodeURIComponent(companyId)}/tracker-templates/${encodeURIComponent(templateId)}`,
      (headers) => ({ method: "GET", headers }),
      "tracker-templates:get",
      user,
    );
    if (!resp.ok) {
      console.warn("[api:tracker-templates:get] http error", resp.status, resp.statusText);
      return null;
    }
    if (!isJsonContent(resp)) return null;
    const json = await parseJsonSafely<any>(resp, "tracker-templates:get");
    return json?.template ?? null;
  } catch (e) {
    console.error("[api:tracker-templates:get] error", e);
    return null;
  }
}

export async function createCompanyTrackerTemplate(
  companyId: string,
  templateName: string,
  discipline: string,
  milestones: any[],
  user?: UserHeaders,
): Promise<{ id: string; company_id: string; template_name: string; discipline: string; milestones: any[]; updated_at: string; updated_by: string | null }> {
  if (!companyId) throw new Error("companyId is required");
  console.debug("[api:tracker-templates:create] request", { companyId, templateName, discipline });
  const resp = await fetchWithAuthRetry(
    `/api/companies/${encodeURIComponent(companyId)}/tracker-templates`,
    (headers) => ({ method: "POST", headers, body: JSON.stringify({ template_name: templateName, discipline, milestones }) }),
    "tracker-templates:create",
    user,
  );
  logDebug(resp, "tracker-templates:create");
  if (!resp.ok) {
    let text = "";
    try { text = await resp.text(); } catch {}
    console.error("[api:tracker-templates:create] http error", resp.status, resp.statusText, text.slice(0, 200));
    if (resp.status === 409) {
      let msg = "A template with this name and discipline already exists";
      try { const j = JSON.parse(text); if (j?.error) msg = j.error; } catch {}
      throw new Error(msg);
    }
    throw new Error(`Failed to create tracker template: ${resp.status} ${resp.statusText}`);
  }
  const json = await parseJsonSafely<any>(resp, "tracker-templates:create");
  return json?.template ?? json;
}

export async function updateCompanyTrackerTemplate(
  companyId: string,
  templateId: string,
  body: { template_name?: string; discipline?: string; milestones?: any[]; updated_at?: string },
  user?: UserHeaders,
): Promise<{ id: string; company_id: string; template_name: string; discipline: string; milestones: any[]; updated_at: string; updated_by: string | null }> {
  if (!companyId || !templateId) throw new Error("companyId and templateId are required");
  console.debug("[api:tracker-templates:update] request", { companyId, templateId });
  const resp = await fetchWithAuthRetry(
    `/api/companies/${encodeURIComponent(companyId)}/tracker-templates/${encodeURIComponent(templateId)}`,
    (headers) => ({ method: "PUT", headers, body: JSON.stringify(body) }),
    "tracker-templates:update",
    user,
  );
  logDebug(resp, "tracker-templates:update");
  if (!resp.ok) {
    let text = "";
    try { text = await resp.text(); } catch {}
    console.error("[api:tracker-templates:update] http error", resp.status, resp.statusText, text.slice(0, 200));
    if (resp.status === 409) {
      let msg = "A template with this name already exists";
      try { const j = JSON.parse(text); if (j?.error) msg = j.error; } catch {}
      throw new Error(msg);
    }
    throw new Error(`Failed to update tracker template: ${resp.status} ${resp.statusText}`);
  }
  const json = await parseJsonSafely<any>(resp, "tracker-templates:update");
  return json?.template ?? json;
}

export async function deleteCompanyTrackerTemplate(
  companyId: string,
  templateId: string,
  user?: UserHeaders,
): Promise<void> {
  if (!companyId || !templateId) throw new Error("companyId and templateId are required");
  console.debug("[api:tracker-templates:delete] request", { companyId, templateId });
  const resp = await fetchWithAuthRetry(
    `/api/companies/${encodeURIComponent(companyId)}/tracker-templates/${encodeURIComponent(templateId)}`,
    (headers) => ({ method: "DELETE", headers }),
    "tracker-templates:delete",
    user,
  );
  logDebug(resp, "tracker-templates:delete");
  if (!resp.ok) {
    let text = "";
    try { text = await resp.text(); } catch {}
    console.error("[api:tracker-templates:delete] http error", resp.status, resp.statusText, text.slice(0, 200));
    throw new Error(`Failed to delete tracker template: ${resp.status} ${resp.statusText}`);
  }
}

// --- Notes API (via CF Worker proxy) ---

export async function listNotes(user?: UserHeaders): Promise<Note[]> {
  console.debug("[api:notes:list] request");
  const key = "cache:notes:list";
  const ttlMs = 30_000;
  return withCache<Note[]>(key, ttlMs, async () => {
    let logged = false;
    const resp = await fetchWithAuthRetry(
      "/api/notes",
      (headers) => {
        if (!logged) {
          logged = true;
          const headersObj = headersToLogObject(headers);
          console.log("[DEBUG:notes:list] REQUEST", {
            url: "/api/notes",
            method: "GET",
            headers: headersObj,
            userParam: user,
          });
        }
        return { headers };
      },
      "notes:list",
      user,
    );
    const respHeadersObj: Record<string, string> = {};
    resp.headers.forEach((value, key) => { respHeadersObj[key] = value; });
    let bodyText = "";
    try {
      bodyText = await resp.clone().text();
    } catch {}
    console.log("[DEBUG:notes:list] RESPONSE", {
      status: resp.status,
      statusText: resp.statusText,
      headers: respHeadersObj,
      body: bodyText.slice(0, 2000),
    });
    if (!resp.ok) {
      console.error("[api:notes:list] http error", resp.status, resp.statusText);
      throw new Error(`Failed to list notes: ${resp.status}`);
    }
    const json = await parseJsonSafely<any>(resp, "notes:list");
    return normalizeArrayResponse<any>(json).map(normalizeNoteKeys) as Note[];
  });
}

export async function getNoteById(id: string, user?: UserHeaders): Promise<Note | null> {
  console.log("[source-pill:notes:get] requesting note", { id });
  const resp = await fetchWithAuthRetry(
    `/api/notes/${encodeURIComponent(id)}`,
    (headers) => ({ headers }),
    "notes:get",
    user,
  );
  console.log("[source-pill:notes:get] response", { id, status: resp.status, ok: resp.ok });
  if (resp.status === 404) {
    console.warn("[source-pill:notes:get] 404 — note not found for id:", id);
    return null;
  }
  if (!resp.ok) await throwApiError(resp, "notes:get");
  const json = await parseJsonSafely<any>(resp, "notes:get");
  const raw = json?.note ?? json;
  console.log("[source-pill:notes:get] parsed", { id, hasNote: !!raw, keys: raw ? Object.keys(raw) : [] });
  return raw ? (normalizeNoteKeys(raw) as Note) : null;
}

export async function createNote(input: CreateNoteInput, user?: UserHeaders): Promise<Note> {
  const bodyObj = compactNoteInput(input);
  console.debug("[api:notes:create] request", { inputPreview: { ...bodyObj, content: (input.content || "").slice(0, 80) } });
  const resp = await fetchWithAuthRetry(
    "/api/notes",
    (headers) => ({ method: "POST", headers, body: JSON.stringify(bodyObj) }),
    "notes:create",
    user,
  );
  logDebug(resp, "notes:create");
  if (!resp.ok) {
    console.error("[api:notes:create] http error", resp.status, resp.statusText);
    await throwApiError(resp, "notes:create");
  }
  const json = await parseJsonSafely<any>(resp, "notes:create");
  requestCache.invalidate("cache:notes:list");
  return normalizeNoteKeys(json.note || json);
}

export async function updateNote(id: string, changes: UpdateNoteInput, user?: UserHeaders): Promise<Note> {
  const bodyObj = compactNoteInput(changes as any);
  console.debug("[api:notes:update] request", { id, changesPreview: { ...bodyObj, content: (changes as any)?.content?.slice?.(0, 80) } });
  const resp = await fetchWithAuthRetry(
    `/api/notes/${encodeURIComponent(id)}`,
    (headers) => ({ method: "PATCH", headers, body: JSON.stringify(bodyObj) }),
    "notes:update",
    user,
  );
  logDebug(resp, "notes:update");
  if (!resp.ok) {
    console.error("[api:notes:update] http error", resp.status, resp.statusText);
    await throwApiError(resp, "notes:update");
  }
  const json = await parseJsonSafely<any>(resp, "notes:update");
  requestCache.invalidate("cache:notes:list");
  return normalizeNoteKeys(json.note || json);
}

export async function deleteNote(id: string, user?: UserHeaders): Promise<{ ok: true }> {
  console.debug("[api:notes:delete] request", { id });
  const resp = await fetchWithAuthRetry(
    `/api/notes/${encodeURIComponent(id)}`,
    (headers) => ({ method: "DELETE", headers }),
    "notes:delete",
    user,
  );
  logDebug(resp, "notes:delete");
  if (!resp.ok) {
    console.error("[api:notes:delete] http error", resp.status, resp.statusText);
    await throwApiError(resp, "notes:delete");
  }
  const res = await parseJsonSafely<{ ok: true }>(resp, "notes:delete");
  requestCache.invalidate("cache:notes:list");
  return res;
}

// --- Notes/Tasks sharing API (via CF Worker proxy) ---

export async function listNoteShares(
  noteId: string,
  params: { includeRevoked?: boolean } = {},
  user?: UserHeaders,
): Promise<ListSharesResponse> {
  const sp = new URLSearchParams();
  if (params.includeRevoked) sp.set("include_revoked", "true");
  const url = `/api/notes/${encodeURIComponent(noteId)}/shares${sp.toString() ? `?${sp.toString()}` : ""}`;
  const resp = await fetchWithAuthRetry(
    url,
    (headers) => ({ method: "GET", headers }),
    "notes:shares:list",
    user,
  );
  if (!resp.ok) await throwApiError(resp, "notes:shares:list");
  return parseJsonSafely<ListSharesResponse>(resp, "notes:shares:list");
}

export async function createNoteShare(
  noteId: string,
  input: { principal_user_id: string; permission: "read" | "write" },
  user?: UserHeaders,
): Promise<{ share: Share }> {
  const body = {
    principal_type: "user",
    principal_user_id: input.principal_user_id,
    permission: input.permission,
  };
  const resp = await fetchWithAuthRetry(
    `/api/notes/${encodeURIComponent(noteId)}/shares`,
    (headers) => ({ method: "POST", headers, body: JSON.stringify(body) }),
    "notes:shares:create",
    user,
  );
  if (!resp.ok) await throwApiError(resp, "notes:shares:create");
  return parseJsonSafely<{ share: Share }>(resp, "notes:shares:create");
}

export async function revokeNoteShare(noteId: string, shareId: string, user?: UserHeaders): Promise<{ share?: Share; ok?: true }> {
  const resp = await fetchWithAuthRetry(
    `/api/notes/${encodeURIComponent(noteId)}/shares/${encodeURIComponent(shareId)}`,
    (headers) => ({ method: "DELETE", headers }),
    "notes:shares:revoke",
    user,
  );
  if (!resp.ok) await throwApiError(resp, "notes:shares:revoke");
  return parseJsonSafely<any>(resp, "notes:shares:revoke") as { share?: Share; ok?: true };
}

export async function dismissNoteShare(noteId: string, shareId: string, user?: UserHeaders): Promise<{ ok: true }> {
  const resp = await fetchWithAuthRetry(
    `/api/notes/${encodeURIComponent(noteId)}/shares/${encodeURIComponent(shareId)}/dismiss`,
    (headers) => ({ method: "POST", headers }),
    "notes:shares:dismiss",
    user,
  );
  if (!resp.ok) await throwApiError(resp, "notes:shares:dismiss");
  return parseJsonSafely<{ ok: true }>(resp, "notes:shares:dismiss");
}

export async function listTaskShares(
  taskId: string,
  params: { includeRevoked?: boolean } = {},
  user?: UserHeaders,
): Promise<ListSharesResponse> {
  const sp = new URLSearchParams();
  if (params.includeRevoked) sp.set("include_revoked", "true");
  const url = `/api/tasks/${encodeURIComponent(taskId)}/shares${sp.toString() ? `?${sp.toString()}` : ""}`;
  const resp = await fetchWithAuthRetry(
    url,
    (headers) => ({ method: "GET", headers }),
    "tasks:shares:list",
    user,
  );
  if (!resp.ok) await throwApiError(resp, "tasks:shares:list");
  return parseJsonSafely<ListSharesResponse>(resp, "tasks:shares:list");
}

export async function createTaskShare(
  taskId: string,
  input: { principal_user_id: string; permission: "read" | "write" },
  user?: UserHeaders,
): Promise<{ share: Share }> {
  const body = {
    principal_type: "user",
    principal_user_id: input.principal_user_id,
    permission: input.permission,
  };
  const resp = await fetchWithAuthRetry(
    `/api/tasks/${encodeURIComponent(taskId)}/shares`,
    (headers) => ({ method: "POST", headers, body: JSON.stringify(body) }),
    "tasks:shares:create",
    user,
  );
  if (!resp.ok) await throwApiError(resp, "tasks:shares:create");
  return parseJsonSafely<{ share: Share }>(resp, "tasks:shares:create");
}

export async function revokeTaskShare(taskId: string, shareId: string, user?: UserHeaders): Promise<{ share?: Share; ok?: true }> {
  const resp = await fetchWithAuthRetry(
    `/api/tasks/${encodeURIComponent(taskId)}/shares/${encodeURIComponent(shareId)}`,
    (headers) => ({ method: "DELETE", headers }),
    "tasks:shares:revoke",
    user,
  );
  if (!resp.ok) await throwApiError(resp, "tasks:shares:revoke");
  return parseJsonSafely<any>(resp, "tasks:shares:revoke") as { share?: Share; ok?: true };
}

export async function dismissTaskShare(taskId: string, shareId: string, user?: UserHeaders): Promise<{ ok: true }> {
  const resp = await fetchWithAuthRetry(
    `/api/tasks/${encodeURIComponent(taskId)}/shares/${encodeURIComponent(shareId)}/dismiss`,
    (headers) => ({ method: "POST", headers }),
    "tasks:shares:dismiss",
    user,
  );
  if (!resp.ok) await throwApiError(resp, "tasks:shares:dismiss");
  return parseJsonSafely<{ ok: true }>(resp, "tasks:shares:dismiss");
}

// ---------------------------------------------------------------------------
// Timesheets
// ---------------------------------------------------------------------------

export async function getOrCreateTimesheet(
  periodStart: string,
  user?: UserHeaders,
): Promise<Timesheet> {
  const resp = await fetchWithAuthRetry(
    "/api/timesheets",
    (headers) => ({ method: "POST", headers, body: JSON.stringify({ period_start: periodStart }) }),
    "timesheets:get-or-create",
    user,
  );
  if (!resp.ok) await throwApiError(resp, "timesheets:get-or-create");
  const data = await parseJsonSafely<Timesheet>(resp, "timesheets:get-or-create");
  requestCache.invalidatePattern("cache:timesheets:");
  return data;
}

export async function listTimesheets(
  params: { userId?: string; periodStart?: string } = {},
  user?: UserHeaders,
): Promise<Timesheet[]> {
  const sp = new URLSearchParams();
  if (params.userId) sp.set("user_id", params.userId);
  if (params.periodStart) sp.set("period_start", params.periodStart);
  const url = "/api/timesheets" + (sp.toString() ? `?${sp.toString()}` : "");
  const cacheKey = `cache:timesheets:list:${url}`;
  return withCache(cacheKey, 30_000, async () => {
    const resp = await fetchWithAuthRetry(
      url,
      (headers) => ({ method: "GET", headers }),
      "timesheets:list",
      user,
    );
    if (!resp.ok) await throwApiError(resp, "timesheets:list");
    const data = await parseJsonSafely<Timesheet[]>(resp, "timesheets:list");
    return Array.isArray(data) ? data : [];
  });
}

export async function getTimesheet(id: string, user?: UserHeaders): Promise<Timesheet | null> {
  const resp = await fetchWithAuthRetry(
    `/api/timesheets/${encodeURIComponent(id)}`,
    (headers) => ({ method: "GET", headers }),
    "timesheets:get",
    user,
  );
  if (resp.status === 404) return null;
  if (!resp.ok) await throwApiError(resp, "timesheets:get");
  return parseJsonSafely<Timesheet>(resp, "timesheets:get");
}

export async function submitTimesheet(id: string, user?: UserHeaders): Promise<Timesheet> {
  const resp = await fetchWithAuthRetry(
    `/api/timesheets/${encodeURIComponent(id)}/submit`,
    (headers) => ({ method: "POST", headers, body: JSON.stringify({ action: "submit" }) }),
    "timesheets:submit",
    user,
  );
  if (!resp.ok) await throwApiError(resp, "timesheets:submit");
  const data = await parseJsonSafely<Timesheet>(resp, "timesheets:submit");
  requestCache.invalidatePattern("cache:timesheets:");
  return data;
}

export async function reviewTimesheet(
  id: string,
  action: "approve" | "reject",
  rejectionNote?: string,
  user?: UserHeaders,
): Promise<Timesheet> {
  const resp = await fetchWithAuthRetry(
    `/api/timesheets/${encodeURIComponent(id)}/review`,
    (headers) => ({
      method: "POST",
      headers,
      body: JSON.stringify({ action, rejection_note: rejectionNote ?? null }),
    }),
    "timesheets:review",
    user,
  );
  if (!resp.ok) await throwApiError(resp, "timesheets:review");
  const data = await parseJsonSafely<Timesheet>(resp, "timesheets:review");
  requestCache.invalidatePattern("cache:timesheets:");
  return data;
}

export async function listTimeEntries(timesheetId: string, user?: UserHeaders): Promise<TimeEntry[]> {
  const cacheKey = `cache:timesheets:entries:${timesheetId}`;
  return withCache(cacheKey, 15_000, async () => {
    const resp = await fetchWithAuthRetry(
      `/api/timesheets/${encodeURIComponent(timesheetId)}/entries`,
      (headers) => ({ method: "GET", headers }),
      "timesheets:entries:list",
      user,
    );
    if (!resp.ok) await throwApiError(resp, "timesheets:entries:list");
    const data = await parseJsonSafely<TimeEntry[]>(resp, "timesheets:entries:list");
    return Array.isArray(data) ? data : [];
  });
}

export async function createTimeEntry(
  timesheetId: string,
  input: CreateTimesheetEntryInput,
  user?: UserHeaders,
): Promise<TimeEntry> {
  const resp = await fetchWithAuthRetry(
    `/api/timesheets/${encodeURIComponent(timesheetId)}/entries`,
    (headers) => ({ method: "POST", headers, body: JSON.stringify(input) }),
    "timesheets:entries:create",
    user,
  );
  if (!resp.ok) await throwApiError(resp, "timesheets:entries:create");
  const data = await parseJsonSafely<TimeEntry>(resp, "timesheets:entries:create");
  requestCache.invalidate(`cache:timesheets:entries:${timesheetId}`);
  return data;
}

export async function updateTimeEntry(
  timesheetId: string,
  entryId: string,
  changes: UpdateTimesheetEntryInput,
  user?: UserHeaders,
): Promise<TimeEntry> {
  const resp = await fetchWithAuthRetry(
    `/api/timesheets/${encodeURIComponent(timesheetId)}/entries/${encodeURIComponent(entryId)}`,
    (headers) => ({ method: "PATCH", headers, body: JSON.stringify(changes) }),
    "timesheets:entries:update",
    user,
  );
  if (!resp.ok) await throwApiError(resp, "timesheets:entries:update");
  const data = await parseJsonSafely<TimeEntry>(resp, "timesheets:entries:update");
  requestCache.invalidate(`cache:timesheets:entries:${timesheetId}`);
  return data;
}

export async function deleteTimeEntry(
  timesheetId: string,
  entryId: string,
  user?: UserHeaders,
): Promise<void> {
  const resp = await fetchWithAuthRetry(
    `/api/timesheets/${encodeURIComponent(timesheetId)}/entries/${encodeURIComponent(entryId)}`,
    (headers) => ({ method: "DELETE", headers }),
    "timesheets:entries:delete",
    user,
  );
  if (!resp.ok) await throwApiError(resp, "timesheets:entries:delete");
  requestCache.invalidate(`cache:timesheets:entries:${timesheetId}`);
}

// --- Owner Requirements API ---

export async function getOwnerRequirements(
  projectId: string,
  user?: UserHeaders,
): Promise<OwnerRequirementDoc[]> {
  if (!projectId) return [];
  console.debug("[api:owner-requirements:get]", { projectId });
  const key = `cache:owner-requirements:${projectId}`;
  const ttlMs = 30_000;
  return withCache<OwnerRequirementDoc[]>(key, ttlMs, async () => {
    try {
      const resp = await fetchWithAuthRetry(
        `/api/projects/${encodeURIComponent(projectId)}/owner-requirements`,
        (headers) => ({ method: "GET", headers }),
        "owner-requirements:get",
        user,
      );
      if (!resp.ok) {
        console.warn("[api:owner-requirements:get] http error", resp.status);
        return [];
      }
      const json = await parseJsonSafely<any>(resp, "owner-requirements:get");
      return Array.isArray(json?.owner_requirements)
        ? json.owner_requirements.map(normalizeOwnerRequirementDoc)
        : [];
    } catch (e) {
      console.error("[api:owner-requirements:get] error", e);
      return [];
    }
  });
}

export async function upsertOwnerRequirement(
  projectId: string,
  discipline: string,
  items: OwnerRequirementState[],
  user?: UserHeaders,
): Promise<OwnerRequirementDoc> {
  if (!projectId) throw new Error("projectId is required");
  console.debug("[api:owner-requirements:upsert]", { projectId, discipline });
  const body = JSON.stringify({ discipline, items });
  const resp = await fetchWithAuthRetry(
    `/api/projects/${encodeURIComponent(projectId)}/owner-requirements`,
    (headers) => ({ method: "PUT", headers, body }),
    "owner-requirements:upsert",
    user,
  );
  if (!resp.ok) {
    await throwApiError(resp, "owner-requirements:upsert");
  }
  const json = await parseJsonSafely<any>(resp, "owner-requirements:upsert");
  requestCache.invalidate(`cache:owner-requirements:${projectId}`);
  return normalizeOwnerRequirementDoc(json?.owner_requirement);
}

export async function deleteOwnerRequirement(
  projectId: string,
  discipline: string,
  user?: UserHeaders,
): Promise<void> {
  if (!projectId) throw new Error("projectId is required");
  console.debug("[api:owner-requirements:delete]", { projectId, discipline });
  const resp = await fetchWithAuthRetry(
    `/api/projects/${encodeURIComponent(projectId)}/owner-requirements/${encodeURIComponent(discipline)}`,
    (headers) => ({ method: "DELETE", headers }),
    "owner-requirements:delete",
    user,
  );
  if (!resp.ok) await throwApiError(resp, "owner-requirements:delete");
  requestCache.invalidate(`cache:owner-requirements:${projectId}`);
}

// --- Owner Requirement Templates API ---

export async function listOwnerReqTemplates(
  companyId: string,
  filters: { city?: string; state?: string; discipline?: string } = {},
  user?: UserHeaders,
): Promise<OwnerRequirementTemplate[]> {
  if (!companyId) return [];
  const sp = new URLSearchParams();
  if (filters.city) sp.set("city", filters.city.trim().toUpperCase());
  if (filters.state) sp.set("state", filters.state.trim().toUpperCase());
  if (filters.discipline) sp.set("discipline", filters.discipline);
  const qs = sp.toString() ? `?${sp.toString()}` : "";
  const key = `cache:owner-req-templates:${companyId}:${qs}`;
  const ttlMs = 60_000;
  return withCache<OwnerRequirementTemplate[]>(key, ttlMs, async () => {
    try {
      // The backend list route intentionally returns summary rows; fetch a
      // specific template by ID before applying it so item payloads stay lazy.
      const resp = await fetchWithAuthRetry(
        `/api/companies/${encodeURIComponent(companyId)}/owner-requirement-templates${qs}`,
        (headers) => ({ method: "GET", headers }),
        "owner-req-templates:list",
        user,
      );
      if (!resp.ok) return [];
      const json = await parseJsonSafely<any>(resp, "owner-req-templates:list");
      return Array.isArray(json?.templates) ? json.templates.map(normalizeOwnerRequirementTemplate) : [];
    } catch (e) {
      console.error("[api:owner-req-templates:list] error", e);
      return [];
    }
  });
}

export async function getOwnerReqTemplate(
  companyId: string,
  templateId: string,
  user?: UserHeaders,
): Promise<OwnerRequirementTemplate> {
  if (!companyId || !templateId) throw new Error("companyId and templateId are required");
  const key = `cache:owner-req-templates:${companyId}:detail:${templateId}`;
  const ttlMs = 60_000;
  return withCache<OwnerRequirementTemplate>(key, ttlMs, async () => {
    const resp = await fetchWithAuthRetry(
      `/api/companies/${encodeURIComponent(companyId)}/owner-requirement-templates/${encodeURIComponent(templateId)}`,
      (headers) => ({ method: "GET", headers }),
      "owner-req-templates:get",
      user,
    );
    if (!resp.ok) {
      await throwApiError(resp, "owner-req-templates:get");
    }
    const json = await parseJsonSafely<any>(resp, "owner-req-templates:get");
    return normalizeOwnerRequirementTemplate(json?.template);
  });
}

export async function createOwnerReqTemplate(
  companyId: string,
  input: {
    city: string;
    state: string;
    discipline: string;
    items: OwnerRequirementState[];
  },
  user?: UserHeaders,
): Promise<OwnerRequirementTemplate> {
  if (!companyId) throw new Error("companyId is required");
  const resp = await fetchWithAuthRetry(
    `/api/companies/${encodeURIComponent(companyId)}/owner-requirement-templates`,
    (headers) => ({ method: "POST", headers, body: JSON.stringify(input) }),
    "owner-req-templates:create",
    user,
  );
  if (!resp.ok) await throwApiError(resp, "owner-req-templates:create");
  const json = await parseJsonSafely<any>(resp, "owner-req-templates:create");
  requestCache.invalidatePattern(`cache:owner-req-templates:${companyId}`);
  return normalizeOwnerRequirementTemplate(json?.template);
}

export async function updateOwnerReqTemplate(
  companyId: string,
  templateId: string,
  input: Partial<{
    city: string;
    state: string;
    discipline: string;
    items: OwnerRequirementState[];
  }>,
  user?: UserHeaders,
): Promise<OwnerRequirementTemplate> {
  if (!companyId || !templateId) throw new Error("companyId and templateId are required");
  const resp = await fetchWithAuthRetry(
    `/api/companies/${encodeURIComponent(companyId)}/owner-requirement-templates/${encodeURIComponent(templateId)}`,
    (headers) => ({ method: "PUT", headers, body: JSON.stringify(input) }),
    "owner-req-templates:update",
    user,
  );
  if (!resp.ok) await throwApiError(resp, "owner-req-templates:update");
  const json = await parseJsonSafely<any>(resp, "owner-req-templates:update");
  requestCache.invalidatePattern(`cache:owner-req-templates:${companyId}`);
  return normalizeOwnerRequirementTemplate(json?.template);
}

export async function deleteOwnerReqTemplate(
  companyId: string,
  templateId: string,
  user?: UserHeaders,
): Promise<void> {
  if (!companyId || !templateId) throw new Error("companyId and templateId are required");
  const resp = await fetchWithAuthRetry(
    `/api/companies/${encodeURIComponent(companyId)}/owner-requirement-templates/${encodeURIComponent(templateId)}`,
    (headers) => ({ method: "DELETE", headers }),
    "owner-req-templates:delete",
    user,
  );
  if (!resp.ok) await throwApiError(resp, "owner-req-templates:delete");
  requestCache.invalidatePattern(`cache:owner-req-templates:${companyId}`);
}
